import { cn } from "../../lib/utils";
import type { Theme, ResolvedTheme } from "../../hooks/useTheme";

interface ThemeToggleProps {
  theme: Theme;
  resolved: ResolvedTheme;
  onThemeChange: (theme: Theme) => void;
  className?: string;
}

const options: { value: Theme; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

export function ThemeToggle({ theme, resolved, onThemeChange, className }: ThemeToggleProps) {
  return (
    <div className={cn("flex items-center gap-[var(--spacing-1)] p-[var(--spacing-0_5)] rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] border border-[var(--color-border-stroke)]", className)}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onThemeChange(opt.value)}
          className={cn(
            "relative flex items-center justify-center gap-[var(--spacing-1)]",
            "rounded-[var(--radius-sm)] px-[var(--spacing-2)] py-[var(--spacing-1)]",
            "text-[13px] font-medium cursor-pointer",
            "transition-colors duration-[var(--duration-fast)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            theme === opt.value
              ? "bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] shadow-sm"
              : "text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]",
          )}
          aria-pressed={theme === opt.value}
        >
          <ThemeIcon mode={opt.value} resolved={resolved} active={theme === opt.value} />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ThemeIcon({ mode, resolved, active }: { mode: Theme; resolved: ResolvedTheme; active: boolean }) {
  const color = active ? "currentColor" : "currentColor";

  if (mode === "light") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
      </svg>
    );
  }

  if (mode === "dark") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
      </svg>
    );
  }

  // System
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
